import React, { useState, useEffect } from "react";
import {
  Typography,
  Box,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormHelperText,
} from "@mui/material";
import ActionButtons from "../../../Components/Quetionary/InitialKickoff/MaterialTopics/ActionButtons";
import { useNavigate, useParams } from "react-router-dom";
import { get, put } from "../../../utils/api";
import { numberError, textError } from "../../../utils/validationMessage";
import MainDashboardLayout from "../../../Layout/MainDashboardLayout";
import GovernanceLayout from "../../../Layout/GovernanceLayout";
import {
  fixedPercentage,
  formatNumberInputWithCommas,
  stripNumberFormatting,
} from "../../../utils/common";

const ComplianceForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [complianceViolations, setComplianceViolations] = useState("");
  const [ethicalViolations, setEthicalViolations] = useState("");
  const [nonMonetarySanctions, setNonMonetarySanctions] = useState("");
  const [totalFinesAndPenalties, setTotalFinesAndPenalties] = useState("");
  const [ethicsTraining, setEthicsTraining] = useState("yes");
  const [trainedEmployees, setTrainedEmployees] = useState("");
  const [totalEmployees, setTotalEmployees] = useState("");
  const [percentage, setPercentage] = useState("");
  
  const [errors, setErrors] = useState({});

  const [currency, setCurrency] = useState("Currency");

  const clearError = (field) => {
    setErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handlePercentageChange = (e, field) => {
    const value = formatNumberInputWithCommas(e.target.value);
    const numericRaw = stripNumberFormatting(value);

    // Numeric-only typing guard
    if (numericRaw !== "" && !/^\d+$/.test(numericRaw)) return;

    // Check if the value is a valid number
    if (!numericRaw || isNaN(numericRaw) || Number(numericRaw) < 0) {
      if (field === "trained") {
        setTrainedEmployees("");
      } else if (field === "total") {
        setTotalEmployees("");
      }
      setPercentage("");
      return;
    }

    const parsedValue = parseFloat(numericRaw);

    // For total employees, check if value is less than 1
    if (field === "total" && parsedValue < 1) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        totalEmployees: "Total number of employees must be at least 1.",
      }));
      setTotalEmployees("");
      setPercentage("");
      return;
    }

    if (field === "trained") {
      setTrainedEmployees(value);
      clearError("trainedEmployees");
    } else if (field === "total") {
      setTotalEmployees(value);
      clearError("totalEmployees");
    }

    const newTrainedEmployees =
      field === "trained"
        ? parsedValue
        : parseFloat(stripNumberFormatting(trainedEmployees));
    const newTotalEmployees =
      field === "total"
        ? parsedValue
        : parseFloat(stripNumberFormatting(totalEmployees));

    // Check if trainedEmployees is greater than totalEmployees
    if (newTrainedEmployees > newTotalEmployees) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        trainedEmployees: "This field cannot exceed from total.",
      }));
      setPercentage(""); // Reset percentage
    } else {
      setErrors((prevErrors) => {
        const newErrors = { ...prevErrors };
        delete newErrors.trainedEmployees;
        delete newErrors.totalEmployees;
        return newErrors;
      });

      // Calculate percentage only when values are valid
      if (newTotalEmployees >= 1 && newTrainedEmployees >= 0) {
        const calculatedPercentage = (
          (newTrainedEmployees / newTotalEmployees) *
          100
        ).toFixed(3);
        setPercentage(calculatedPercentage);
      } else {
        setPercentage("");
      }
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await get(`/governance/getGovernance?id=${id}`);

        const data = response.data?.data?.[0]?.complianceAndEthics || {};

        setComplianceViolations(
          data.complianceViolations !== undefined &&
            data.complianceViolations !== null &&
            data.complianceViolations !== ""
            ? formatNumberInputWithCommas(String(data.complianceViolations))
            : "",
        );
        setEthicalViolations(
          data.instancesFinesPaid !== undefined &&
            data.instancesFinesPaid !== null &&
            data.instancesFinesPaid !== ""
            ? formatNumberInputWithCommas(String(data.instancesFinesPaid))
            : "",
        );
        setNonMonetarySanctions(
          data.nonMonetarySanctions !== undefined &&
            data.nonMonetarySanctions !== null &&
            data.nonMonetarySanctions !== ""
            ? formatNumberInputWithCommas(String(data.nonMonetarySanctions))
            : data.ethicalViolations !== undefined &&
                data.ethicalViolations !== null &&
                data.ethicalViolations !== ""
              ? formatNumberInputWithCommas(String(data.ethicalViolations))
              : "",
        );
        // setTotalFinesAndPenalties(data.totalFinesAndPenalties || "");
        setTotalFinesAndPenalties(
          data.totalFinesAndPenalties?.amount !== undefined &&
            data.totalFinesAndPenalties?.amount !== null &&
            data.totalFinesAndPenalties?.amount !== ""
            ? formatNumberInputWithCommas(
                String(data.totalFinesAndPenalties.amount),
              )
            : "",
        );
        // setCurrency(data.totalFinesAndPenalties?.currency || "Currency");
        setEthicsTraining(
          typeof data.hasEthicsTrainingProgram?.status === "boolean"
            ? data.hasEthicsTrainingProgram.status
              ? "yes"
              : "no"
            : "yes"
        );
  
        setTrainedEmployees(
          data.percentageEmployeesTrainedInEthics?.trainedEmployees !==
            undefined &&
            data.percentageEmployeesTrainedInEthics?.trainedEmployees !==
              null &&
            data.percentageEmployeesTrainedInEthics?.trainedEmployees !== ""
            ? formatNumberInputWithCommas(
                String(
                  data.percentageEmployeesTrainedInEthics?.trainedEmployees,
                ),
              )
            : "",
        );
        setTotalEmployees(
          data.percentageEmployeesTrainedInEthics?.totalEmployees !==
            undefined &&
            data.percentageEmployeesTrainedInEthics?.totalEmployees !== null &&
            data.percentageEmployeesTrainedInEthics?.totalEmployees !== ""
            ? formatNumberInputWithCommas(
                String(data.percentageEmployeesTrainedInEthics?.totalEmployees),
              )
            : "",
        );
        setPercentage(
          data.percentageEmployeesTrainedInEthics?.performance?.replace(
            "%",
            ""
          ) || ""
        );
      } catch (error) {
        console.error("Error fetching data:", error.response || error);
      }
    };

    fetchData();
  }, []);

  const validateNumbers = (value) => {
    const raw = stripNumberFormatting(value);
    return /^\d+$/.test(raw) || raw === ""; // Allow only positive integers or empty
  };

  const handleTextChange = (e, field, setter, validator) => {
    const value = formatNumberInputWithCommas(e.target.value);
    const raw = stripNumberFormatting(value);

    // Numeric/text guard based on provided validator (blocks invalid typing)
    if (raw !== "" && !validator(value)) return;

    // Set value and clear error for valid values
    setter(value);
    clearError(field);

    // Validate and clear errors if valid
    if (validator(value)) {
      setErrors((prevErrors) => {
        const newErrors = { ...prevErrors };
        delete newErrors[field]; // Remove error for the specific field
        return newErrors;
      });
    }
  };

  // const handleCurrencyChange = (value) => {
  //   setCurrency(value);
  //   if (value && value !== "Currency") {
  //     clearError("currency");
  //   }
  // };

  const validateForm = () => {
    const newErrors = {};

    if (complianceViolations === "") {
      newErrors.complianceViolations = textError;
    } else if (!validateNumbers(complianceViolations)) {
      newErrors.complianceViolations = numberError;
    }

    if (ethicalViolations === "") {
      newErrors.ethicalViolations = textError;
    } else if (!validateNumbers(ethicalViolations)) {
      newErrors.ethicalViolations = numberError;
    }

    if (nonMonetarySanctions === "") {
      newErrors.nonMonetarySanctions = textError;
    } else if (!validateNumbers(nonMonetarySanctions)) {
      newErrors.nonMonetarySanctions = numberError;
    }

    if (totalFinesAndPenalties === "") {
      newErrors.totalFinesAndPenalties = textError;
    } else if (!validateNumbers(totalFinesAndPenalties)) {
      newErrors.totalFinesAndPenalties = numberError;
    }

    if (trainedEmployees === "") {
      newErrors.trainedEmployees = textError;
    } else if (!validateNumbers(trainedEmployees)) {
      newErrors.trainedEmployees = numberError;
    }

    if (totalEmployees === "") {
      newErrors.totalEmployees = textError;
    } else if (!validateNumbers(totalEmployees)) {
      newErrors.totalEmployees = numberError;
    } else if (parseFloat(stripNumberFormatting(totalEmployees)) < 1) {
      newErrors.totalEmployees = "Total number of employees must be at least 1.";
    }

    // if (!currency || currency === "Currency") {
    //   newErrors.currency = "Please select a valid currency.";
    // }

    // Add validation to ensure totalEmployees > trainedEmployees
    if (
      parseFloat(stripNumberFormatting(totalEmployees)) <
      parseFloat(stripNumberFormatting(trainedEmployees))
    ) {
      newErrors.totalEmployees =
        "Total employees must be greater than trained employees.";
    }

    if (
      !percentage ||
      isNaN(percentage) ||
      percentage < 0 ||
      percentage > 100
    ) {
      newErrors.percentage = "Please enter a valid percentage.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    const requestData = {
      complianceViolations: parseInt(stripNumberFormatting(complianceViolations), 10),
      ethicalViolations: parseInt(stripNumberFormatting(nonMonetarySanctions), 10),
      instancesFinesPaid: parseInt(stripNumberFormatting(ethicalViolations), 10),
      nonMonetarySanctions: parseInt(stripNumberFormatting(nonMonetarySanctions), 10),
      // totalFinesAndPenalties: parseInt(totalFinesAndPenalties, 10),
      totalFinesAndPenalties: {
        // currency,
        amount: parseInt(stripNumberFormatting(totalFinesAndPenalties), 10),
      },
      hasEthicsTrainingProgram: {
        status: ethicsTraining === "yes",
      },
      percentageEmployeesTrainedInEthics: {
        trainedEmployees: parseInt(stripNumberFormatting(trainedEmployees), 10),
        totalEmployees: parseInt(stripNumberFormatting(totalEmployees), 10),
        performance: `${percentage}%`,
      },
      stepsCompleted: {
        complianceAndEthics: true,
      },
    };

    try {
      await put(`/governance/updateGovernance`, {
        id: id,
        complianceAndEthics: requestData,
        stepsCompleted: {
          complianceAndEthics: true,
        },
      })
        .then((response) => {
          navigate(`/governance/data/shareholder-rights/${id}`);
        })
        .catch((error) => {
          console.error("Error submitting data:", error);
        });
    } catch (error) {
      console.error(
        "Error submitting compliance data:",
        error.response?.data || error.message
      );
    }
  };

  const handleBack = () => {
    navigate(`/governance/data/risk-management/${id}`);
  };

  const handleFileChange = (file) => {
    // Set the uploaded file URL

    // Remove the error once a file is uploaded
    setErrors((prevErrors) => {
      const newErrors = { ...prevErrors }; // Remove
      return newErrors;
    });
  };

  return (
    <MainDashboardLayout sidebar={<GovernanceLayout />}>
      <Box
        sx={{
          margin: "20px auto",
          padding: "20px",
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "#000",
          },
          "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#000",
          },
          "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#1976d2",
          },
        }}
      >
        <Box>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: "bold",
              margin: "1.2% 0",
              textDecoration: "underline",
            }}
          >
            C. COMPLIANCE AND ETHICS
          </Typography>

          <Box sx={{ margin: "2% 0 1% 0", width: "55%" }}>
            <Typography sx={{ fontWeight: "bold", margin: "1% 0" }}>
              1. Number of Significant Instances of non-compliance
            </Typography>
            <TextField
              fullWidth
              value={complianceViolations}
              // onChange={(e) => setComplianceViolations(e.target.value)}
              onChange={(e) => {
                const next = e.target.value;
                if (next !== "" && !/^\d+$/.test(next)) return;
                handleTextChange(
                  e,
                  "complianceViolations",
                  setComplianceViolations,
                  validateNumbers
                );
              }}
              placeholder="Number of Significant"
              variant="outlined"
              error={!!errors.complianceViolations}
              inputProps={{
                inputMode: "numeric",
                pattern: "[0-9]*",
              }}
              sx={{ "& .MuiInputBase-root": { height: 40 }, ml: "1.1rem" }}
            />
            <FormHelperText
              sx={{ color: "#D34040", fontSize: "14px", marginLeft: "1.1rem" }}
            >
              {errors.complianceViolations}
            </FormHelperText>
          </Box>

          <Box sx={{ margin: "2% 0 1% 0", width: "90%" }}>
            <Typography sx={{ fontWeight: "bold", margin: "1% 0" }}>
              (a) Instances for which fines were paid (In Rupees)
            </Typography>

            <Box sx={{ display: "flex", gap: 2 }}>
              <Box sx={{ width: "30%", ml: "1.1rem" }}>
                <TextField
                  fullWidth
                  value={ethicalViolations}
                  onChange={(e) =>
                    handleTextChange(
                      e,
                      "ethicalViolations",
                      setEthicalViolations,
                      validateNumbers
                    )
                  }
                  placeholder="Number of instance"
                  variant="outlined"
                  error={!!errors.ethicalViolations}
                  inputProps={{
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                  }}
                  sx={{ "& .MuiInputBase-root": { height: 40 }}}
                />
                <FormHelperText sx={{ color: "#D34040", fontSize: "14px", minHeight: 20 }}>
                  {errors.ethicalViolations || " "}
                </FormHelperText>
              </Box>

              <Box sx={{ width: "29%" }}>
                <TextField
                  fullWidth
                  value={totalFinesAndPenalties}
                  onChange={(e) =>
                    handleTextChange(
                      e,
                      "totalFinesAndPenalties",
                      setTotalFinesAndPenalties,
                      validateNumbers
                    )
                  }
                  placeholder="Enter Amount"
                  variant="outlined"
                  error={!!errors.totalFinesAndPenalties}
                  inputProps={{
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                  }}
                  sx={{ "& .MuiInputBase-root": { height: 40 } }}
                />
                <FormHelperText sx={{ color: "#D34040", fontSize: "14px", minHeight: 20 }}>
                  {errors.totalFinesAndPenalties || " "}
                </FormHelperText>
              </Box>

              {/* <Box sx={{ width: "20%" }}>
                <TextField
                  select
                  value={currency}
                  onChange={(e) => handleCurrencyChange(e.target.value)}
                  fullWidth
                  SelectProps={{
                    native: true,
                  }}
                  error={!!errors.currency}
                  sx={{
                    "& .MuiInputBase-root": { height: 40 },
                  }}
                >
                  <option value="Currency">Currency</option>
                  <option value="INR">INR</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="AED">AED</option>
                  <option value="GBP">GBP</option>
                </TextField>
                <FormHelperText sx={{ color: "#D34040", fontSize: "14px", minHeight: 20 }}>
                  {errors.currency || " "}
                </FormHelperText>
              </Box> */}
            </Box>
          </Box>

          <Box sx={{ margin: "2% 0 1% 0", width: "55%" }}>
            <Typography sx={{ fontWeight: "bold", margin: "1% 0" }}>
              (b) Instances for which non-monetary sanctions were incurred
            </Typography>
            <TextField
              fullWidth
              value={nonMonetarySanctions}
              onChange={(e) =>
                handleTextChange(
                  e,
                  "nonMonetarySanctions",
                  setNonMonetarySanctions,
                  validateNumbers
                )
              }
              placeholder="Number of instance"
              variant="outlined"
              error={!!errors.nonMonetarySanctions}
              inputProps={{
                inputMode: "numeric",
                pattern: "[0-9]*",
              }}
              sx={{ "& .MuiInputBase-root": { height: 40 }, ml: "1.1rem" }}
            />
            <FormHelperText
              sx={{ color: "#D34040", fontSize: "14px", marginLeft: "1.1rem" }}
            >
              {errors.nonMonetarySanctions}
            </FormHelperText>
          </Box>

          {/* <Box sx={{ margin: "2% 0 1% 0" }}>
            <Typography sx={{ fontWeight: "bold", margin: "1% 0" }}>
              2. Existence of Risk Management Committee
            </Typography>
            <RadioGroup
              row
              sx={{ ml: "1.1rem" }}
              value={ethicsTraining}
              onChange={(e) => setEthicsTraining(e.target.value)}
            >
              <FormControlLabel
                value="yes"
                control={<Radio sx={{ "&.Mui-checked": { color: "#1c486b" } }} />}
                label="Yes"
              />
              <FormControlLabel
                value="no"
                control={<Radio sx={{ "&.Mui-checked": { color: "#1c486b" } }} />}
                label="No"
              />
            </RadioGroup>

          </Box> */}
        </Box>

        <Box sx={{ margin: "2% 0 1% 0", width: "100%" }}>
          <Typography
            sx={{ fontWeight: "bold", marginBottom: "8px", marginTop: "1%" }}
          >
            2. Percentage of Employees Trained in Ethics
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "16px",
              marginTop: "8px",
              ml:"16px"
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "4px",
                width: "30%",
              }}
            >
              <Typography sx={{ fontWeight: "bold", margin: "1% 0" }}>
                Total Number of Employees:
              </Typography>
              <TextField
                value={totalEmployees}
                placeholder="Enter Number"
                onChange={(e) => handlePercentageChange(e, "total")}
                error={!!errors.totalEmployees}
                inputProps={{
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                }}
                sx={{ "& .MuiInputBase-root": { height: 40, width: "100%" } }}
              />
              <FormHelperText sx={{ color: "#D34040", fontSize: "14px", minHeight: 20 }}>
                {errors.totalEmployees || " "}
              </FormHelperText>
             
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "4px",
                width: "30%",
                ml: "1.1rem",
              }}
            >
              <Typography sx={{ fontWeight: "bold", margin: "1% 0" }}>
                No. of Employees Trained:
              </Typography>
              <TextField
                value={trainedEmployees}
                placeholder="Enter Number"
                error={!!errors.trainedEmployees}
                onChange={(e) => handlePercentageChange(e, "trained")}
                inputProps={{
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                }}
                sx={{ "& .MuiInputBase-root": { height: 40, width: "100%" } }}
              />
              <FormHelperText sx={{ color: "#D34040", fontSize: "14px", minHeight: 20 }}>
                {errors.trainedEmployees || " "}
              </FormHelperText>
              
            </Box>

            

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "4px",
                width: "30%",
              }}
            >
              <Typography sx={{ fontWeight: "bold",marginTop: "-2rem" }}>Percentage:</Typography>
              <TextField
                value={percentage ? `${fixedPercentage(percentage)} %` : ""}
                placeholder="Percentage"
                InputProps={{
                  readOnly: true,
                  disableUnderline: true,
                }}
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: 40,
                    width: "100%",
                    border: "none",
                    "& fieldset": {
                      border: "none",
                    },
                  },
                }}
              />
            </Box>
          </Box>
        </Box>

        <ActionButtons Back={handleBack} Move={handleSubmit} />
      </Box>
    </MainDashboardLayout>
  );
};

export default ComplianceForm;
